import {getBackendErrorMessage} from '../../shared/utils/httpError';

export const getBackendMessage = (error, fallbackMessage) => getBackendErrorMessage(error, fallbackMessage);

export const formatMoneyBR = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('pt-BR', {style: 'currency', currency: 'BRL'}).format(amount);
};

export const normalizeFinanceiroDashboard = (payload = {}) => ({
    mrr: Number(payload?.mrr ?? payload?.receitaMensal ?? payload?.receita_mensal ?? 0),
    pendingInvoices: Number(payload?.faturasPendentes ?? payload?.faturas_pendentes ?? payload?.pendingInvoices ?? 0),
    overdueInvoices: Number(payload?.faturasVencidas ?? payload?.faturas_vencidas ?? payload?.overdueInvoices ?? 0),
    openAmount: Number(payload?.valorEmAberto ?? payload?.valor_em_aberto ?? payload?.openAmount ?? 0),
});

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const toNumberOrNull = (value) => (value == null || value === '' ? null : toNumber(value));

// ---------------------------------------------------------------------------------------------
// Datas. Vencimento e competência são datas de calendário: '2026-10-10' é dia 10 em qualquer
// fuso, e não passa por `new Date`, que leria meia-noite UTC e mostraria dia 9 no Brasil.
// ---------------------------------------------------------------------------------------------

export const FUSO_DO_BRASIL = 'America/Sao_Paulo';

const DATA_NO_INICIO = /^(\d{4})-(\d{2})-(\d{2})/;
const COM_FUSO = /(Z|[+-]\d{2}:?\d{2})$/i;
const pad2 = (value) => String(value).padStart(2, '0');

/** O dia de hoje (ou de `instante`) em São Paulo, como 'aaaa-mm-dd'. */
export const diaEmSaoPaulo = (instante = new Date()) => {
    const partes = new Intl.DateTimeFormat('en-CA', {
        timeZone: FUSO_DO_BRASIL,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instante);
    const parte = (tipo) => partes.find((item) => item.type === tipo)?.value;
    return `${parte('year')}-${parte('month')}-${parte('day')}`;
};

/**
 * Data de calendário 'aaaa-mm-dd'. Aceita a data pura, data e hora (com fuso, vira o dia de São
 * Paulo; sem fuso, vale o dia escrito) e o formato em lista do Jackson ([aaaa, m, d]).
 */
export const paraDataISO = (value) => {
    if (Array.isArray(value) && value.length >= 3) {
        const [ano, mes, dia] = value;
        return `${ano}-${pad2(mes)}-${pad2(dia)}`;
    }
    if (typeof value !== 'string') return null;

    const texto = value.trim();
    const data = DATA_NO_INICIO.exec(texto);
    if (!data) return null;

    if (texto.length > 10 && COM_FUSO.test(texto)) {
        const instante = new Date(texto);
        if (!Number.isNaN(instante.getTime())) return diaEmSaoPaulo(instante);
    }
    return `${data[1]}-${data[2]}-${data[3]}`;
};

export const formatDateBR = (value) => {
    const iso = paraDataISO(value);
    if (!iso) return '-';
    const [ano, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${ano}`;
};

/** Competência como "Outubro/2026". */
export const formatCompetencia = (value) => {
    const iso = paraDataISO(value);
    if (!iso) return '-';
    const [ano, mes] = iso.split('-');
    const nomeDoMes = new Intl.DateTimeFormat('pt-BR', {month: 'long', timeZone: 'UTC'})
        .format(new Date(Date.UTC(Number(ano), Number(mes) - 1, 1)));
    return `${nomeDoMes.charAt(0).toUpperCase()}${nomeDoMes.slice(1)}/${ano}`;
};

export const formatPeriodo = (inicio, fim) => {
    if (!paraDataISO(inicio) && !paraDataISO(fim)) return null;
    return `${formatDateBR(inicio)} a ${formatDateBR(fim)}`;
};

// ---------------------------------------------------------------------------------------------
// Fatura (app-faturamento)
// ---------------------------------------------------------------------------------------------

const SEM_SITUACAO = {className: 'bf-pill bf-pill--muted'};

export const SITUACOES_DA_FATURA = {
    ABERTA: {label: 'Em aberto', className: 'bf-pill bf-pill--primary'},
    PAGA_EM_PARTE: {label: 'Paga em parte', className: 'bf-pill bf-pill--accent'},
    VENCIDA: {label: 'Vencida', className: 'bf-pill bf-pill--danger'},
    PAGA: {label: 'Paga', className: 'bf-pill bf-pill--success'},
    CANCELADA: {label: 'Cancelada', className: 'bf-pill bf-pill--muted'},
};

export const FILTROS_DA_FATURA = [
    {value: '', label: 'Todas'},
    {value: 'ABERTA', label: 'Em aberto'},
    {value: 'VENCIDA', label: 'Vencidas'},
    {value: 'PAGA_EM_PARTE', label: 'Pagas em parte'},
    {value: 'PAGA', label: 'Pagas'},
    {value: 'CANCELADA', label: 'Canceladas'},
];

export const situacaoDaFatura = (status) => (
    SITUACOES_DA_FATURA[String(status || '').toUpperCase()] || {...SEM_SITUACAO, label: status || '-'}
);

export const nomeDaEmpresa = (fatura = {}) => fatura?.nome_fantasia || fatura?.razao_social || '-';

export const normalizarFatura = (item = {}) => ({
    id: item?.id,
    numero: item?.numero || null,
    empresaId: item?.empresa_id || null,
    empresa: nomeDaEmpresa(item),
    razaoSocial: item?.razao_social || null,
    cnpj: item?.cnpj || null,
    competencia: item?.competencia || null,
    referencia: item?.referencia || null,
    emitidaEm: item?.emitida_em || null,
    vencimento: item?.vencimento || null,
    periodoInicio: item?.periodo_inicio || null,
    periodoFim: item?.periodo_fim || null,
    valorTotal: toNumber(item?.valor_total),
    status: item?.status || null,
});

/** `data: {items, pagination}` do `GET /financeiro/faturas`. */
export const extrairPaginaDeFaturas = (response) => {
    const data = response?.data?.data ?? {};
    const items = Array.isArray(data?.items) ? data.items : [];
    const pagination = data?.pagination || {};
    return {
        items: items.map(normalizarFatura),
        pagination: {
            page: toNumber(pagination.page) || 1,
            total: toNumber(pagination.total ?? items.length),
            totalPages: Math.max(1, toNumber(pagination.total_pages) || 1),
            hasNext: Boolean(pagination.has_next),
            hasPrevious: Boolean(pagination.has_previous),
        },
    };
};

// O que o PDF mostra em cada linha: `referencia_detalhada` é um objeto ({o_que, explicacao}).
const normalizarItem = (item = {}) => {
    const referencia = item?.referencia_detalhada && typeof item.referencia_detalhada === 'object'
        ? item.referencia_detalhada
        : {};
    return {
        id: item?.id,
        descricao: referencia.o_que || item?.descricao || '-',
        explicacao: typeof referencia.explicacao === 'string' ? referencia.explicacao : null,
        valor: toNumber(item?.valor),
    };
};

/**
 * `data` do `GET /financeiro/faturas/{id}`: uma seção por assinatura, com as linhas e o total
 * dela. Fatura antiga, de antes das seções, traz só `itens`: eles vão em `itensSemSecao`.
 */
export const normalizarDetalheDaFatura = (data = {}) => {
    const secoes = (Array.isArray(data?.secoes) ? data.secoes : [])
        .slice()
        .sort((a, b) => toNumber(a?.ordem) - toNumber(b?.ordem))
        .map((secao) => ({
            id: secao?.id,
            licencaId: secao?.licenca_id || null,
            plano: secao?.plano_nome || null,
            chaveAtivacao: secao?.chave_ativacao || null,
            periodoInicio: secao?.periodo_inicio || null,
            periodoFim: secao?.periodo_fim || null,
            total: toNumber(secao?.total),
            itens: (Array.isArray(secao?.itens) ? secao.itens : []).map(normalizarItem),
        }));
    const idsDasSecoes = new Set(secoes.map((secao) => secao.id));
    const itensSemSecao = (Array.isArray(data?.itens) ? data.itens : [])
        .filter((item) => !item?.secao_id || !idsDasSecoes.has(item.secao_id))
        .map(normalizarItem);

    return {
        fatura: normalizarFatura(data?.fatura || {}),
        secoes,
        itensSemSecao,
    };
};

export const tituloDaSecao = (secao = {}) => {
    const plano = secao.plano ? `Plano ${secao.plano}` : 'Assinatura';
    return secao.chaveAtivacao ? `${plano} · licença ${secao.chaveAtivacao}` : plano;
};

// ---------------------------------------------------------------------------------------------
// Cobranças (app-financeiro, camelCase e sem envelope)
// ---------------------------------------------------------------------------------------------

export const SITUACOES_DA_COBRANCA = {
    ABERTA: {label: 'Em aberto', className: 'bf-pill bf-pill--primary'},
    RECUSADA: {label: 'Recusada', className: 'bf-pill bf-pill--danger'},
    PAGA: {label: 'Paga', className: 'bf-pill bf-pill--success'},
    ESTORNADA: {label: 'Estornada', className: 'bf-pill bf-pill--muted'},
    CHARGEBACK: {label: 'Contestada (chargeback)', className: 'bf-pill bf-pill--warning'},
    CANCELADA: {label: 'Cancelada', className: 'bf-pill bf-pill--muted'},
};

const COBRANCA_VENCIDA = {label: 'Vencida', className: 'bf-pill bf-pill--danger'};

export const MEIOS_DA_COBRANCA = {
    BOLETO: 'Boleto com Pix',
    CARTAO: 'Cartão de crédito',
};

export const meioDaCobranca = (meio) => MEIOS_DA_COBRANCA[meio] || meio || '-';

/** Em aberto e com o vencimento antes de hoje, no dia de São Paulo. */
export const cobrancaVencida = (cobranca, hoje = diaEmSaoPaulo()) => {
    const vencimento = paraDataISO(cobranca?.vencimento);
    return cobranca?.situacao === 'ABERTA' && Boolean(vencimento) && vencimento < hoje;
};

export const situacaoDaCobranca = (cobranca, hoje = diaEmSaoPaulo()) => {
    if (cobrancaVencida(cobranca, hoje)) return COBRANCA_VENCIDA;
    return SITUACOES_DA_COBRANCA[cobranca?.situacao] || {...SEM_SITUACAO, label: cobranca?.situacao || '-'};
};

/** A lista do `GET /financeiro/cobrancas?fatura_id=`, na ordem do vencimento. */
export const extrairCobrancas = (response) => {
    const data = response?.data;
    const lista = Array.isArray(data) ? data : [];
    return lista.slice().sort((a, b) => String(paraDataISO(a?.vencimento) || '').localeCompare(String(paraDataISO(b?.vencimento) || '')));
};

// Boleto que ainda serve para pagar. VENCIDO também: pago depois, cobra multa e juros.
const BOLETO_PARA_PAGAR = ['EMITIDO', 'REGISTRADO', 'VENCIDO'];

export const AVISO_BOLETO_EM_REGISTRO = 'O boleto está sendo registrado no banco. Ele aparece aqui em alguns minutos.';
export const AVISO_BOLETO_INDISPONIVEL = 'O boleto desta cobrança não está disponível para pagamento agora. Se precisar, fale com a Busifarm.';
export const AVISO_DE_ENCARGOS = 'Pago depois do vencimento, o boleto cobra multa de 2% e juros de 1% ao mês.';

/**
 * O que a tela mostra para pagar a cobrança (`tipo`):
 * - BOLETO: em aberto, com boleto que serve para pagar: linha digitável, Pix e PDF do boleto, e o
 *   aviso de encargos se já venceu;
 * - BOLETO_EM_REGISTRO: em aberto, sem boleto ainda (ou sem os dados dele);
 * - BOLETO_INDISPONIVEL: em aberto, com um boleto que não serve mais (pago, cancelado, baixado);
 * - PAGA: data e valor pago, com os encargos, se houve;
 * - NADA: cartão (fica para outra história) e cobrança recusada, estornada, contestada ou cancelada.
 * Nenhum valor é calculado aqui: só o que a API mandou.
 */
export const pagamentoDaCobranca = (cobranca, hoje = diaEmSaoPaulo()) => {
    if (cobranca?.meio !== 'BOLETO') return {tipo: 'NADA'};

    if (cobranca.situacao === 'PAGA') {
        return {
            tipo: 'PAGA',
            pagaEm: cobranca.pagaEm || null,
            valorPago: toNumberOrNull(cobranca.valorPago),
            valorEncargos: toNumber(cobranca.valorEncargos),
        };
    }
    if (cobranca.situacao !== 'ABERTA') return {tipo: 'NADA'};

    const boleto = cobranca.boleto;
    if (!boleto) return {tipo: 'BOLETO_EM_REGISTRO'};
    if (!BOLETO_PARA_PAGAR.includes(boleto.status)) return {tipo: 'BOLETO_INDISPONIVEL'};
    if (!boleto.linhaDigitavel && !boleto.pixCopiaECola && !boleto.urlPdf) return {tipo: 'BOLETO_EM_REGISTRO'};

    return {
        tipo: 'BOLETO',
        linhaDigitavel: boleto.linhaDigitavel || null,
        pixCopiaECola: boleto.pixCopiaECola || null,
        urlPdf: boleto.urlPdf || null,
        vencida: cobrancaVencida(cobranca, hoje),
    };
};

/** "R$ 1.020,00, com R$ 20,00 de multa e juros" (o trecho dos encargos só quando houve). */
export const descreverValorPago = ({valorPago, valorEncargos} = {}) => {
    if (valorPago == null) return null;
    const pago = formatMoneyBR(valorPago);
    return valorEncargos > 0 ? `${pago}, com ${formatMoneyBR(valorEncargos)} de multa e juros` : pago;
};

/** CNPJ (14 dígitos) ou CPF (11) com a máscara; outro formato volta como veio. */
export const formatDocumento = (value) => {
    if (!value) return '-';
    const digitos = String(value).replace(/\D/g, '');
    if (digitos.length === 14) return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (digitos.length === 11) return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return String(value);
};
